/**
 * Settings Page
 * Account info + change password + change email + delete account.
 */

import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthenticationContext';
import './SettingsPage.css';

const SettingsPage = () => {
  const { user, token, setUser, logout, applyToken } = useAuth();
  const navigate = useNavigate();

  const [pwForm, setPwForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [pwStatus, setPwStatus] = useState(null);
  const [pwSaving, setPwSaving] = useState(false);

  const [emailForm, setEmailForm] = useState({ newEmail: '', password: '' });
  const [emailStatus, setEmailStatus] = useState(null);
  const [emailSaving, setEmailSaving] = useState(false);

  const [emailPrefs, setEmailPrefs] = useState({ dailyPromptReminder: true, streakReminder: true });
  const [prefsStatus, setPrefsStatus] = useState(null);

  const [deletePassword, setDeletePassword] = useState('');
  const [deleteStatus, setDeleteStatus] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Preferences arrive with the user object (missing means default: on).
  useEffect(() => {
    if (user) {
      setEmailPrefs({
        dailyPromptReminder: user.emailPreferences?.dailyPromptReminder !== false,
        streakReminder: user.emailPreferences?.streakReminder !== false
      });
    }
  }, [user]);

  if (!token) {
    return (
      <div className="container">
        <h1>Settings</h1>
        <p><Link to="/login">Log in</Link> to manage your account.</p>
      </div>
    );
  }

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setPwStatus(null);
    if (pwForm.newPassword !== pwForm.confirmPassword) {
      setPwStatus({ type: 'error', text: 'New passwords do not match.' });
      return;
    }
    if (pwForm.newPassword.length < 6) {
      setPwStatus({ type: 'error', text: 'New password must be at least 6 characters.' });
      return;
    }
    setPwSaving(true);
    try {
      const res = await axios.post('/api/auth/change-password', {
        currentPassword: pwForm.currentPassword,
        newPassword: pwForm.newPassword
      });
      // Server rotates the session on password change; adopt the new token so
      // this device stays logged in (other devices are signed out).
      if (res.data.token) applyToken(res.data.token);
      setPwStatus({ type: 'success', text: res.data.message });
      setPwForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      setPwStatus({ type: 'error', text: error.response?.data?.message || 'Could not change password.' });
    } finally {
      setPwSaving(false);
    }
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setEmailStatus(null);
    setEmailSaving(true);
    try {
      const res = await axios.post('/api/auth/change-email', {
        newEmail: emailForm.newEmail,
        password: emailForm.password
      });
      setEmailStatus({ type: 'success', text: res.data.message });
      setUser({ ...user, email: res.data.email });
      setEmailForm({ newEmail: '', password: '' });
    } catch (error) {
      setEmailStatus({ type: 'error', text: error.response?.data?.message || 'Could not change email.' });
    } finally {
      setEmailSaving(false);
    }
  };

  const handleTogglePref = async (key) => {
    const previous = emailPrefs;
    const next = { ...emailPrefs, [key]: !emailPrefs[key] };
    setEmailPrefs(next);
    setPrefsStatus(null);
    try {
      const res = await axios.put('/api/users/email-preferences', next);
      setUser({ ...user, emailPreferences: res.data.emailPreferences });
    } catch (error) {
      setEmailPrefs(previous);
      setPrefsStatus({ type: 'error', text: error.response?.data?.message || 'Could not save email preferences.' });
    }
  };

  const handleDeleteAccount = async (e) => {
    e.preventDefault();
    setDeleteStatus(null);
    if (!window.confirm('Permanently delete your account and all your posts? This cannot be undone.')) {
      return;
    }
    setDeleting(true);
    try {
      await axios.delete('/api/auth/account', { data: { password: deletePassword } });
      logout();
      navigate('/');
    } catch (error) {
      setDeleteStatus({ type: 'error', text: error.response?.data?.message || 'Could not delete account.' });
      setDeleting(false);
    }
  };

  return (
    <div className="container settings-page">
      <h1>Settings</h1>

      <section className="settings-section">
        <h2>Account</h2>
        <div className="settings-account">
          <div><span className="settings-label">Username</span><span>@{user?.username}</span></div>
          <div><span className="settings-label">Email</span><span>{user?.email}</span></div>
        </div>
      </section>

      <section className="settings-section">
        <h2>Change password</h2>
        {pwStatus && <div className={`save-status ${pwStatus.type}`}>{pwStatus.text}</div>}
        <form onSubmit={handlePasswordSubmit} className="settings-form">
          <div className="form-group">
            <label>Current password</label>
            <input
              type="password"
              value={pwForm.currentPassword}
              onChange={(e) => setPwForm({ ...pwForm, currentPassword: e.target.value })}
              autoComplete="current-password"
              required
            />
          </div>
          <div className="form-group">
            <label>New password</label>
            <input
              type="password"
              value={pwForm.newPassword}
              onChange={(e) => setPwForm({ ...pwForm, newPassword: e.target.value })}
              autoComplete="new-password"
              minLength={6}
              required
            />
          </div>
          <div className="form-group">
            <label>Confirm new password</label>
            <input
              type="password"
              value={pwForm.confirmPassword}
              onChange={(e) => setPwForm({ ...pwForm, confirmPassword: e.target.value })}
              autoComplete="new-password"
              required
            />
          </div>
          <button type="submit" className="btn-save" disabled={pwSaving}>
            {pwSaving ? 'Saving...' : 'Update password'}
          </button>
        </form>
      </section>

      <section className="settings-section">
        <h2>Change email</h2>
        {emailStatus && <div className={`save-status ${emailStatus.type}`}>{emailStatus.text}</div>}
        <form onSubmit={handleEmailSubmit} className="settings-form">
          <div className="form-group">
            <label>New email</label>
            <input
              type="email"
              value={emailForm.newEmail}
              onChange={(e) => setEmailForm({ ...emailForm, newEmail: e.target.value })}
              autoComplete="email"
              required
            />
          </div>
          <div className="form-group">
            <label>Current password</label>
            <input
              type="password"
              value={emailForm.password}
              onChange={(e) => setEmailForm({ ...emailForm, password: e.target.value })}
              autoComplete="current-password"
              required
            />
          </div>
          <button type="submit" className="btn-save" disabled={emailSaving}>
            {emailSaving ? 'Saving...' : 'Update email'}
          </button>
        </form>
      </section>

      <section className="settings-section">
        <h2>Email preferences</h2>
        {prefsStatus && <div className={`save-status ${prefsStatus.type}`}>{prefsStatus.text}</div>}
        <div className="settings-toggle-row">
          <div>
            <span className="settings-toggle-title">Daily prompt reminder</span>
            <span className="settings-toggle-hint">Get each day's writing prompt in your inbox at 9am UK time.</span>
          </div>
          <label className="settings-toggle">
            <input
              type="checkbox"
              checked={emailPrefs.dailyPromptReminder}
              onChange={() => handleTogglePref('dailyPromptReminder')}
            />
            <span className="settings-toggle-slider" />
          </label>
        </div>
        <div className="settings-toggle-row">
          <div>
            <span className="settings-toggle-title">Streak reminder</span>
            <span className="settings-toggle-hint">A nudge at 7pm UK time if your writing streak is about to end.</span>
          </div>
          <label className="settings-toggle">
            <input
              type="checkbox"
              checked={emailPrefs.streakReminder}
              onChange={() => handleTogglePref('streakReminder')}
            />
            <span className="settings-toggle-slider" />
          </label>
        </div>
      </section>

      <section className="settings-section settings-danger">
        <h2>Delete account</h2>
        <p className="settings-hint">
          This permanently removes your account and all of your posts. This cannot be undone.
        </p>
        {deleteStatus && <div className={`save-status ${deleteStatus.type}`}>{deleteStatus.text}</div>}
        <form onSubmit={handleDeleteAccount} className="settings-form">
          <div className="form-group">
            <label>Confirm your password</label>
            <input
              type="password"
              value={deletePassword}
              onChange={(e) => setDeletePassword(e.target.value)}
              autoComplete="current-password"
              required
            />
          </div>
          <button type="submit" className="btn-danger" disabled={deleting}>
            {deleting ? 'Deleting...' : 'Delete my account'}
          </button>
        </form>
      </section>
    </div>
  );
};

export default SettingsPage;
